// PREMIER import, et ce n'est pas du style : voir le module.
import './identity/entreePrecoce'
import { createApp } from 'vue'
import { createPinia } from 'pinia'

import App from './App.vue'
import { router } from './router'
import { installerAnnoncesRoute } from './router/annonces'
import { i18n } from './i18n'
import {
  amorcerImportSessionLegacy,
  currentSession,
  rejeterSessionEtrangere,
  setSessionAdoptedHandler,
  setUnauthorizedHandler,
} from './api/client'
import { attendreAmorcage } from './identity/amorcage'
import { allerALaPorte } from './identity/porte'
import { ecouterPerteIdentite } from './identity/propagation'
import { ouvrirRuntimeIdentite } from './identity/runtime'
import { surveillerSlot } from './identity/surveillance'
import { ecosystemeCourant } from './api/tenant'
import { etatTempsReel, realtime } from './api/realtime'
import { useAuthStore } from './stores/auth'
import { initThemeEager } from './stores/theme'
import { initA11yEager } from './stores/a11y'

// Polices auto-hébergées. Le socle CSS déclare « Inter » et « JetBrains Mono »
// avec repli sur la pile système : sans ces imports l'application reste lisible,
// mais n'a pas l'allure de betool. Seuls les poids réellement utilisés sont
// importés — en ajouter alourdirait le bundle pour rien.
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/jetbrains-mono/400.css'
import './assets/main.css'

// Thème et accessibilité appliqués avant le montage : le script en ligne de
// index.html couvre la première peinture, ceux-ci couvrent la suite. Une police
// qui grossit APRÈS la première peinture fait sauter toute la mise en page sous
// les yeux de l'utilisateur.
initThemeEager()
initA11yEager()

const app = createApp(App)
const pinia = createPinia()

app.use(pinia)
app.use(router)
app.use(i18n)

// APRÈS pinia et i18n : le crochet résout le store d'accessibilité et traduit le
// titre à chaque navigation, donc les deux doivent être installés.
installerAnnoncesRoute(router)

// Le client HTTP ne connaît ni Pinia ni le routeur : il signale simplement
// « la session n'est plus valable » et c'est ici qu'on décide quoi en faire.
// Ce câblage doit venir APRÈS `app.use(pinia)`, sans quoi `useAuthStore()`
// n'aurait pas d'instance active à résoudre.
setUnauthorizedHandler(() => {
  const auth = useAuthStore()
  if (!auth.isAuthenticated) return
  auth.forgetLocally()
  // À la PORTE : cette application n'a plus d'écran de connexion à viser, et
  // `redirect` n'a plus de destinataire — la porte compare l'ORIGINE aux points
  // d'entrée enregistrés, jamais un chemin.
  allerALaPorte()
})

/*
 * SORTIE MUTUELLE : le trousseau est unique et partagé.
 *
 * Se déconnecter depuis une AUTRE application du tenant — Sign, le kiosque —
 * retire la session du trousseau, pour tout le monde. Sans cette écoute, cette
 * page gardait la sienne : écrans pleins, droit de travailler, et un widget qui
 * annonçait « Connectée » une identité qui n'existait plus. Mesuré en
 * production : déconnexion depuis Sign, le kiosque le voyait, pas le
 * backoffice — il était le seul des trois à n'écouter rien.
 *
 * Même geste que le 401 : on coupe SANS appeler le serveur (le jeton est déjà
 * sans valeur) et on renvoie à la connexion. Les deux chemins de sortie mènent
 * donc au même endroit, ce qui est la seule façon qu'ils ne divergent pas.
 */
const runtimePourSortie = ouvrirRuntimeIdentite()
const sortirDeCetteApplication = (): void => {
  const auth = useAuthStore()
  if (!auth.isAuthenticated) return
  auth.forgetLocally()
  if (router.currentRoute.value.name !== 'login') {
    allerALaPorte()
  }
}

// Le SIGNAL : immédiat, mais limité à cette origine — un `BroadcastChannel` ne
// traverse pas de `certif.betool.fr` à `backacces.betool.fr`.
ecouterPerteIdentite(runtimePourSortie, { fermer: sortirDeCetteApplication })

// Le BALAYAGE : la seule protection quand le retrait vient d'une AUTRE
// application du tenant. Il interroge le BFF, seule source que les deux
// partagent. Les deux mécanismes sont nécessaires, et aucun ne remplace
// l'autre — c'est la règle du dépôt sur tout accès authentifié une seule fois.
surveillerSlot(runtimePourSortie, {
  fermer: sortirDeCetteApplication,
  estConnecte: () => useAuthStore().isAuthenticated,
})

// Synchronisation multi-onglets (ADR-006, critère 4) : un autre onglet a fait
// tourner les jetons et nous venons de les ADOPTER. Le seul état qui ne s'en
// remet pas seul est un canal temps réel en « refus » : notre propre rotation
// avait échoué sur un jeton de rafraîchissement que l'autre onglet venait de
// révoquer, et le canal s'est arrêté. Les jetons adoptés sont valides — le
// canal repart, comme au login. Dans les autres états, rien à faire :
// « reconnexion » réessaie tout seul avec la session courante, « pret » n'a
// pas de reprise à faire.
setSessionAdoptedHandler(() => {
  if (etatTempsReel.value === 'refus' && currentSession() !== null) {
    realtime.connect()
  }
})

// Import unique de la session historique (BBG-216 / E6-T2) AVANT la
// vérification d'écosystème, la revalidation et le montage : si ce navigateur
// porte encore le refresh d'avant le trousseau, le slot importé doit être
// RANGÉ côté BFF (cookie posé) avant la première requête de session — sinon
// celle-ci partirait sans porteur et déconnecterait la personne qu'on
// promettait de préserver. Sans clé historique, la promesse est déjà résolue :
// aucun délai n'est ajouté au démarrage (et le mode bearer n'attend que la
// microtache d'une promesse déjà tenue — l'ordre relatif ci-dessous est celui
// d'avant, tous ces démarrages étant des tâches de fond).
void amorcerImportSessionLegacy().then(() => {
  // Vérification de l'ÉCOSYSTÈME (BBG-149 §11.1.2), avant la revalidation.
  //
  // Deux onglets ouverts sur deux tenants ne partagent déjà rien : chaque tenant
  // a son hôte (BBG-73), donc son origine, donc son `localStorage`. Cette
  // vérification ne CRÉE pas l'isolation — le navigateur s'en charge —, elle
  // l'AFFIRME : la session gardée en mémoire doit appartenir à l'écosystème de
  // la page ouverte, et si elle n'y appartient pas, elle part avant qu'une
  // requête ne soit tentée avec elle.
  //
  // Une panne réseau ne déconnecte personne : ne pas joindre le point d'entrée
  // n'apprend rien sur la session, et le serveur reste l'autorité — une session
  // réellement étrangère est refusée à la première requête.
  void ecosystemeCourant()
    .then((ecosysteme) => {
      if (rejeterSessionEtrangere(ecosysteme.id)) {
        // La session appartenait à un AUTRE écosystème : on repart de la PORTE
        // de celui-ci, plutôt que de laisser l'interface se peindre avec des
        // données que le serveur refusera.
        allerALaPorte()
      }
    })
    .catch(() => {
      // Volontairement silencieux : voir `rejeterSessionEtrangere`.
    })

  // Revalidation en tâche de fond : l'interface s'affiche immédiatement avec la
  // session locale, et un jeton révoqué entre-temps (compte désactivé, mot de
  // passe changé sur un autre poste) déclenche la déconnexion dès la réponse.
  // Volontairement non bloquant — attendre le réseau pour peindre le premier
  // écran est ce qui donne l'impression d'une application lente.
  /*
   * APRÈS l'amorçage, et c'est tout le sujet.
   *
   * L'entrée par laissez-passer ouvre la session en deux appels réseau. Lancer
   * la revalidation sans attendre créait le store AVANT que la session
   * n'existe : il naissait vide, et rien ne le remplissait ensuite — barre
   * latérale sans menu, atterrissage sur l'écran de repli, alors que
   * l'application était bel et bien connectée.
   *
   * Non bloquant pour la peinture : `app.mount` reste immédiat, et la garde de
   * routes attend déjà l'amorçage avant de trancher.
   */
  void attendreAmorcage().then(() => useAuthStore(pinia).refreshUser())

  app.mount('#app')
})
